import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet } from '../kit/Sheet';
import { Button } from '../kit/Button';
import { PhotoCapture } from '../form/PhotoCapture';
import { haptic } from '../../lib/haptic';
import { formatISTDate, istDateYmd } from '../../lib/format';

type ExpenseType = 'TOLL' | 'PARKING' | 'PERMIT' | 'OTHER' | 'CLEANING' | 'DRIVER_BATA';
type PaymentSource = 'DRIVER' | 'COMPANY';

export type ExpenseDraft = {
  type: ExpenseType;
  amount: number;
  date: string;
  description: string | null;
  paymentSource: PaymentSource;
  receiptUrl: string | null;
};

type Props = {
  open: boolean;
  // 'add' (default) opens a blank form; 'edit' prefills from `initial` and shows
  // Save + Delete. The parent remounts via a changing `key` so form state
  // re-initialises per expense.
  mode?: 'add' | 'edit';
  initial?: Partial<ExpenseDraft> | null;
  // Lower bound for the expense date (the booking's pickup date, YYYY-MM-DD) —
  // an expense can't predate the trip. Omitted → no lower bound.
  minDate?: string;
  onClose: () => void;
  onConfirm: (data: ExpenseDraft) => void | boolean | Promise<void | boolean>;
  onDelete?: () => void;
};

// Offered categories for NEW expenses. DRIVER_BATA is intentionally omitted —
// a driver's daily allowance is handled via salary, not as a trip expense — but
// it stays in the ExpenseType union so historical DRIVER_BATA rows still
// type-check and render (see the select below, which re-adds the current type
// when editing a row whose type is no longer offered).
const TYPES: ExpenseType[] = ['TOLL', 'PARKING', 'PERMIT', 'CLEANING', 'OTHER'];

function todayIso(): string {
  return istDateYmd(new Date());
}

export function ExpenseSheet({ open, mode = 'add', initial, minDate, onClose, onConfirm, onDelete }: Props) {
  const { t } = useTranslation('trip');
  const isEdit = mode === 'edit';
  const [type, setType] = useState<ExpenseType>(initial?.type ?? 'TOLL');
  const [amount, setAmount] = useState(initial?.amount != null ? String(initial.amount) : '');
  const [date, setDate] = useState(initial?.date ?? todayIso());
  const [description, setDescription] = useState(initial?.description ?? '');
  const [receiptUrl, setReceiptUrl] = useState<string | null>(initial?.receiptUrl ?? null);
  const [submitting, setSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState<'idle' | 'saved' | 'error'>('idle');

  const amountNum = parseFloat(amount);
  const dateValid = /^\d{4}-\d{2}-\d{2}$/.test(date);
  // An expense can't be dated before the booking (ISO strings compare lexically).
  const dateBeforeBooking = !!minDate && dateValid && date < minDate;
  // Toll & parking must carry a receipt photo (GH #504) — verification + anti-
  // misuse. The amount stays required (billing needs the figure; ops can also
  // cross-check it against the receipt).
  const receiptRequired = type === 'TOLL' || type === 'PARKING';
  const isValid = !!type && Number.isFinite(amountNum) && amountNum > 0 && dateValid && !dateBeforeBooking && (!receiptRequired || !!receiptUrl);

  const reset = () => {
    setType('TOLL');
    setAmount('');
    setDate(todayIso());
    setDescription('');
    setReceiptUrl(null);
  };

  const submit = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    setSubmitState('idle');
    try {
      const saved = await onConfirm({
        type,
        amount: amountNum,
        date,
        description: description.trim() || null,
        // Driver-app expenses are always paid by the driver — toggle removed.
        paymentSource: 'DRIVER',
        receiptUrl,
      });
      if (saved === false) {
        setSubmitState('error');
        return;
      }
      haptic.success();
      if (!isEdit) {
        reset();
        setSubmitState('saved');
      }
    } catch {
      setSubmitState('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onClose={() => { if (!submitting) { reset(); onClose(); } }} title={isEdit ? t('edit_expense_title', { defaultValue: 'Edit Expense' }) : t('add_expense_title', { defaultValue: 'Add Expense' })}>
      <div className="space-y-3">
        <label className="block">
          <span className="block text-sm font-medium text-[var(--color-text)] mb-1">{t('expense_type', { defaultValue: 'Type' })}</span>
          {/* appearance-none kills the inconsistent native arrow; a custom
              chevron is overlaid so the control matches the other inputs. */}
          <div className="relative">
            <select
              value={type}
              onChange={(e) => setType(e.target.value as ExpenseType)}
              className="w-full appearance-none pl-3 pr-10 py-2 bg-[var(--color-surface-soft)] border border-[var(--color-border,#d1d5db)] rounded-xl"
            >
              {(TYPES.includes(type) ? TYPES : [type, ...TYPES]).map(tp => <option key={tp} value={tp}>{tp}</option>)}
            </select>
            <svg
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--color-text-secondary)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
        </label>

        {/* min-w-0 on the grid items + inputs: grid tracks default to
            min-width:auto and won't shrink below content, so iOS Safari's wide
            native <input type=date> intrinsic width overflowed the 1fr cell
            off-screen. min-w-0 lets the track + the w-full input shrink to fit. */}
        <div className="grid grid-cols-2 gap-3">
          <label className="block min-w-0">
            <span className="block text-sm font-medium text-[var(--color-text)] mb-1">{t('expense_amount', { defaultValue: 'Amount (₹)' })}</span>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder="0"
              className="w-full min-w-0 px-3 py-2 text-center font-mono bg-[var(--color-surface-soft)] border border-[var(--color-border,#d1d5db)] rounded-xl"
            />
          </label>
          <label className="block min-w-0">
            <span className="block text-sm font-medium text-[var(--color-text)] mb-1">{t('expense_date', { defaultValue: 'Date' })}</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              min={minDate || undefined}
              max={todayIso()}
              className="w-full min-w-0 px-3 py-2 bg-[var(--color-surface-soft)] border border-[var(--color-border,#d1d5db)] rounded-xl"
            />
          </label>
        </div>
        {dateBeforeBooking ? (
          <p className="text-xs text-[var(--color-danger,#C13834)]">
            {t('expense_date_before_booking', { defaultValue: "Date can't be before the booking date" })} ({formatISTDate(minDate)}).
          </p>
        ) : null}

        <label className="block">
          <span className="block text-sm font-medium text-[var(--color-text)] mb-1">{t('expense_description', { defaultValue: 'Description (optional)' })}</span>
          <input
            type="text"
            value={description}
            maxLength={500}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('expense_description_placeholder', { defaultValue: 'e.g., Vashi Toll Plaza' })}
            className="w-full px-3 py-2 bg-[var(--color-surface-soft)] border border-[var(--color-border,#d1d5db)] rounded-xl"
          />
        </label>

        <PhotoCapture
          value={receiptUrl}
          onChange={setReceiptUrl}
          label={receiptRequired
            ? t('receipt_photo_required', { defaultValue: 'Receipt photo (required)' })
            : t('receipt_photo', { defaultValue: 'Receipt photo (optional)' })}
        />
        {receiptRequired && !receiptUrl ? (
          <p className="text-xs text-[var(--color-text-secondary)]">
            {t('receipt_required_hint', { defaultValue: 'Add a receipt for toll & parking — it speeds up verification and avoids disputes.' })}
          </p>
        ) : null}

        {submitState === 'saved' ? (
          <p role="status" className="text-sm font-medium text-[var(--green-700)]">{t('expense_added_continue', { defaultValue: 'Expense added. You can add another.' })}</p>
        ) : null}
        {submitState === 'error' ? (
          <p role="alert" className="text-sm text-[var(--color-danger,#C13834)]">{t('expense_add_failed', { defaultValue: 'Could not save the expense. Please try again.' })}</p>
        ) : null}
        <Button onClick={() => void submit()} disabled={!isValid || submitting} loading={submitting} className="w-full">
          {isEdit ? t('save_expense', { defaultValue: 'Save changes' }) : t('add_expense_confirm', { defaultValue: 'Add expense' })}
        </Button>
        {isEdit && onDelete ? (
          <button
            type="button"
            onClick={() => { haptic.warn(); onDelete(); }}
            className="w-full py-2.5 text-sm font-semibold text-[var(--color-danger,#C13834)]"
          >
            {t('delete_expense', { defaultValue: 'Delete expense' })}
          </button>
        ) : null}
      </div>
    </Sheet>
  );
}
